import React from 'react';
import styled from "styled-components"
import { useTranslation } from 'react-i18next';
import { purpleColors, slateColors, fontFamily } from './ContentPages/AppLandingPage/styles';
import { Clock } from 'lucide-react';
import { RecentSearch } from '../types/search';

const Container = styled.div`
  display: flex;
  padding: 1.5rem 1rem;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  background: white;
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 640px) {
    padding: 2rem 1.5rem;
    gap: 1.25rem;
  }
  
  @media (min-width: 768px) {
    padding: 2rem 3rem;
    gap: 1.5rem;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 80rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (min-width: 640px) {
    gap: 1.25rem;
  }
  
  @media (min-width: 768px) {
    gap: 1.5rem;
  }
`;

const Title = styled.h3`
  font-family: ${fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${slateColors[700]};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

const List = styled.div`
    display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  width: 100%;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ListItem = styled.button`
  font-family: ${fontFamily};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1rem;
  gap: 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid ${slateColors[200]};
  background: white;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  width: 100%;
  
  &:hover {
    border-color: ${purpleColors[300]};
    background: ${slateColors[50]};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  }
`;

const RouteText = styled.div`
  font-family: ${fontFamily};
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${slateColors[900]};
  line-height: 1.4;
  
  @media (min-width: 640px) {
    font-size: 1rem;
  }
`;

const RouteArrow = styled.span`
  color: ${purpleColors[600]};
  margin: 0 0.25rem;
`;

const DateText = styled.div`
  font-family: ${fontFamily};
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: ${slateColors[600]};
  line-height: 1.4;
  
  @media (min-width: 640px) {
    font-size: 0.875rem;
  }
  
  svg {
    width: 0.875rem;
    height: 0.875rem;
    color: ${slateColors[400]};
    flex-shrink: 0;
  }
`;

interface RecentsProps {
  recentSearches: RecentSearch[];
  onSearch: (search: RecentSearch) => void;
}

const formatDate = (dateString: string, t: (key: string) => string) => {
  const searchDate = new Date(dateString);
  const now = new Date();
  const isToday =
    searchDate.getDate() === now.getDate() &&
    searchDate.getMonth() === now.getMonth() &&
    searchDate.getFullYear() === now.getFullYear();
  const isTomorrow =
    searchDate.getDate() === now.getDate() + 1 &&
    searchDate.getMonth() === now.getMonth() &&
    searchDate.getFullYear() === now.getFullYear();

  const timeStr = searchDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const atStr = t('common.at');

  if (isToday) {
    return `${t('common.today')} ${atStr} ${timeStr}`;
  } else if (isTomorrow) {
    return `${t('common.tomorrow')} ${atStr} ${timeStr}`;
  } else {
    const datePart = searchDate.toLocaleDateString([], {
      day: 'numeric',
      month: 'numeric'
    });
    return `${datePart} ${atStr} ${timeStr}`;
  }
};

const getUniqueSearches = (searches: RecentSearch[]): RecentSearch[] => {
  const seen = new Set<string>();
  const uniqueSearches: RecentSearch[] = [];
  
  // Process searches in reverse chronological order (most recent first)
  for (let i = searches.length - 1; i >= 0; i--) {
    const search = searches[i];
    const key = `${search.originId}-${search.destinationId}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueSearches.push(search);
      if (uniqueSearches.length === 4) break; // Stop once we have 5 unique searches
    }
  }
  
  return uniqueSearches; // Already in most-recent-first order
};

const Recents: React.FC<RecentsProps> = ({ recentSearches, onSearch }) => {
  const { t } = useTranslation();
  const uniqueRecentSearches = getUniqueSearches(recentSearches);

  const handleListItemClick = (search: RecentSearch) => {
    onSearch(search);
  };

  if (uniqueRecentSearches.length === 0) {
    return null;
  }

  return (
    <Container>
      <ContentWrapper>
        <Title>{t('recents.searchAgain')}</Title>
      <List>
        {uniqueRecentSearches.map((search, index) => (
          <ListItem key={index} onClick={() => handleListItemClick(search)}>
              <RouteText>
                {search.originName}
                <RouteArrow>→</RouteArrow>
                {search.destinationName}
              </RouteText>
              <DateText>
                <Clock />
                <span>{search.tripType === 'departure' ? t('common.departing') : t('common.arriving')} {formatDate(search.date, t)}</span>
              </DateText>
          </ListItem>
        ))}
      </List>
      </ContentWrapper>
    </Container>
  );
};

export default Recents;
